import { QUESTIONS, scoreToTier, type AttackType } from '../data/questions';
import type { QuizAnswer } from './CheckQuiz';

interface CheckResultProps {
  answers: QuizAnswer[];
  onRetake: () => void;
  onJoinWaitlist: () => void;
}

const TYPE_LABELS: Record<AttackType, { label: string; icon: string }> = {
  phishing: { label: 'Phishing (email)', icon: 'ri-mail-warning-line' },
  smishing: { label: 'Smishing (SMS)', icon: 'ri-message-2-line' },
  vishing: { label: 'Vishing (voice)', icon: 'ri-phone-line' },
};

export function CheckResult({ answers, onRetake, onJoinWaitlist }: CheckResultProps) {
  const correct = answers.filter((a) => a.correct).length;
  const total = QUESTIONS.length;
  const tier = scoreToTier(correct, total);

  const byType: Record<AttackType, { correct: number; total: number }> = {
    phishing: { correct: 0, total: 0 },
    smishing: { correct: 0, total: 0 },
    vishing: { correct: 0, total: 0 },
  };

  QUESTIONS.forEach((q) => {
    byType[q.type].total += 1;
    const answer = answers.find((a) => a.questionId === q.id);
    if (answer?.correct) byType[q.type].correct += 1;
  });

  return (
    <section className="relative pt-32 pb-16">
      <div className="container mx-auto px-6 max-w-3xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 border border-blue-500/30 rounded-full px-4 py-1 mb-8">
            <span className="text-sm text-blue-300">Your result</span>
          </div>

          <div className={`text-6xl md:text-7xl font-light mb-4 ${tier.accentClass}`}>
            {correct} <span className="text-gray-600">/ {total}</span>
          </div>

          <div className={`text-2xl font-medium mb-3 ${tier.accentClass}`}>{tier.label}</div>

          <h2 className="text-3xl md:text-4xl font-light text-white mb-6 max-w-2xl mx-auto leading-tight">
            {tier.headline}
          </h2>

          <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
            {tier.summary}
          </p>
        </div>

        {/* Breakdown */}
        <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 mb-8">
          <h3 className="text-white font-medium mb-6">Breakdown by attack type</h3>
          <div className="space-y-5">
            {(Object.keys(byType) as AttackType[]).map((type) => {
              const { correct: c, total: t } = byType[type];
              const pct = t === 0 ? 0 : (c / t) * 100;
              const info = TYPE_LABELS[type];
              return (
                <div key={type}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <i className={`${info.icon} text-blue-400 text-lg`}></i>
                      <span className="text-white">{info.label}</span>
                    </div>
                    <span className="text-gray-400 text-sm">{c} / {t}</span>
                  </div>
                  <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-400 to-cyan-400 transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-2xl p-8 text-center mb-8">
          <h3 className="text-2xl font-medium text-white mb-3">
            Want a layer that catches what people miss?
          </h3>
          <p className="text-gray-300 mb-6 max-w-xl mx-auto">
            Driscop is building an AI detection feature that flags phishing, smishing and vishing attempts before they reach you. Join the waitlist for early access.
          </p>
          <button
            onClick={onJoinWaitlist}
            className="group px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 cursor-pointer bg-white text-black hover:bg-blue-400 hover:text-white"
          >
            Join the waitlist
            <span className="ml-2 group-hover:translate-y-1 transition-transform duration-300 inline-block">↓</span>
          </button>
        </div>

        <div className="text-center">
          <button
            onClick={onRetake}
            className="text-gray-400 hover:text-blue-400 transition-colors text-sm cursor-pointer"
          >
            <i className="ri-restart-line mr-2"></i>
            Retake the check
          </button>
        </div>
      </div>
    </section>
  );
}
